import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, RefreshCw, Info, ShieldX } from 'lucide-react';
import {
  Button,
  Input,
  Checkbox,
  Badge,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
  Chip,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { ChipListInput } from './ChipListInput';
import styles from './AccessControlTab.module.css';
import {
  useWaapAccessControls,
  useCreateWaapAccessControl,
  useUpdateWaapAccessControl,
  useToggleWaapAccessControl,
  useDeleteWaapAccessControl,
  useWaapRateLimit,
  useSaveWaapRateLimit,
  useDeleteWaapRateLimit,
  type WaapAccessControlRule,
  type WaapAccessControlInput,
  type WaapRateLimit,
} from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';

// Match field (label → vendor `what`). URL Path is the captured default; the
// non-path `what` values follow the vendor's req.* / req.header.<name> convention
// (verify against a capture if a create with one of them is rejected).
const FIELDS = [
  { value: 'req.path', label: 'URL Path' },
  { value: 'req.query', label: 'URL Query String' },
  { value: 'req.method', label: 'Request Method' },
  { value: 'req.host', label: 'Hostname' },
  { value: 'req.header.user-agent', label: 'User Agent' },
  { value: 'req.header.cookie', label: 'Cookie' },
  { value: 'req.header.origin', label: 'Origin' },
  { value: 'req.header.via', label: 'Via' },
  { value: 'client.ip', label: 'Client IP' },
];

// Operator values are the EXACT vendor enum (public Open-API "Create Access
// Control Rule" doc). Negated forms use the not_* underscore form — note
// `not_istartwith` is spelled without the second "s" (vendor literal).
// subnet / not_subnet apply only to the client.ip field.
const OPERATORS = [
  { value: 'startswith', label: 'starts with' },
  { value: 'not_startswith', label: 'does not start with' },
  { value: 'istartswith', label: 'starts with (ignore case)' },
  { value: 'not_istartwith', label: 'does not start with (ignore case)' },
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'iequals', label: 'equals (ignore case)' },
  { value: 'not_iequals', label: 'does not equal (ignore case)' },
  { value: 'endswith', label: 'ends with' },
  { value: 'not_endswith', label: 'does not end with' },
  { value: 'iendswith', label: 'ends with (ignore case)' },
  { value: 'not_iendswith', label: 'does not end with (ignore case)' },
  { value: 'regex', label: 'regex' },
  { value: 'subnet', label: 'in subnet (client.ip only)' },
  { value: 'not_subnet', label: 'not in subnet (client.ip only)' },
];

const fieldLabel = (what: string) => FIELDS.find((f) => f.value === what)?.label ?? what;
const opLabel = (op: string) => OPERATORS.find((o) => o.value === op)?.label ?? op;

const SECTIONS = [
  { type: 'allow', title: 'WhiteList', add: 'Add Whitelist' },
  { type: 'deny', title: 'Blacklist', add: 'Add Blacklist' },
  { type: 'token', title: 'Token Secret Access', add: 'Add Token Access' },
] as const;

function generateToken(len = 64): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const arr = new Uint8Array(len);
  window.crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < len; i += 1) out += chars[arr[i] % chars.length];
  return out;
}

const splitValues = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const matchSummary = (r: WaapAccessControlRule) =>
  r.matches.map((m) => `${fieldLabel(m.field)} ${opLabel(m.operator)} [${m.values.join(', ')}]`).join('; ') || '—';

/** Compact cell for a long comma-separated value list (IP / network restriction).
 *  Shows the first entry plus a "+N" badge; clicking reveals the full, scrollable
 *  list in a popover so the row never overflows horizontally. */
function ValueListCell({ value }: { value: string }) {
  const items = useMemo(() => splitValues(value ?? ''), [value]);
  if (items.length === 0) return <span className={styles.mono}>—</span>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={styles.listTrigger} title="View all entries">
          <span className={styles.listPreview}>{items[0]}</span>
          {items.length > 1 && (
            <Badge tone="neutral" appearance="subtle" size="sm">+{items.length - 1}</Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className={styles.listContent} align="start">
        <div className={styles.listHead}>
          <span>IP / Network</span>
          <span>{items.length} {items.length === 1 ? 'entry' : 'entries'}</span>
        </div>
        <div className={styles.listBody}>
          {items.map((v, i) => (
            <Chip key={`${v}-${i}`} size="sm" appearance="subtle">{v}</Chip>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface FormState {
  name: string;
  field: string;
  operator: string;
  values: string;
  location: string;
  subnet: string;
  anonymousIp: boolean;
  tokenSecret: string;
  priority: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  field: 'req.path',
  operator: 'startswith',
  values: '',
  location: '',
  subnet: '',
  anonymousIp: false,
  tokenSecret: '',
  priority: '',
};

function PolicySection({
  domainId,
  type,
  title,
  addLabel,
  rules,
}: {
  domainId: string;
  type: string;
  title: string;
  addLabel: string;
  rules: WaapAccessControlRule[];
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editing, setEditing] = useState<WaapAccessControlRule | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WaapAccessControlRule | null>(null);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const closeForm = () => {
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };
  const startEdit = (r: WaapAccessControlRule) => {
    setEditing(r);
    setForm({
      name: r.name,
      field: r.matches[0]?.field ?? 'req.path',
      operator: r.matches[0]?.operator ?? 'equals',
      values: (r.matches[0]?.values ?? []).join(', '),
      location: r.geo_restriction ?? '',
      subnet: r.ip_restriction ?? '',
      anonymousIp: r.anonymous_ip ?? false,
      tokenSecret: r.token_secret ?? '',
      priority: r.priority ? String(r.priority) : '',
    });
    setOpen(true);
  };

  const create = useCreateWaapAccessControl(domainId, {
    onSuccess: () => {
      show({ tone: 'success', title: `${title} policy created` });
      closeForm();
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateWaapAccessControl(domainId, {
    onSuccess: () => {
      show({ tone: 'success', title: `${title} policy updated` });
      closeForm();
    },
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const toggle = useToggleWaapAccessControl(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'Policy updated' }),
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const remove = useDeleteWaapAccessControl(domainId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Policy deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const valid = form.name.trim() && splitValues(form.values).length > 0 && (type !== 'token' || form.tokenSecret.trim());

  const submit = () => {
    const body: WaapAccessControlInput = {
      name: form.name.trim(),
      type,
      priority: Number(form.priority) || 0,
      enabled: editing?.enabled ?? true,
      ip_restriction: form.subnet.trim(),
      geo_restriction: form.location.trim(),
      anonymous_ip: type === 'deny' ? form.anonymousIp : false,
      token_secret: type === 'token' ? form.tokenSecret.trim() : '',
      matches: [{ field: form.field, operator: form.operator, values: splitValues(form.values) }],
    };
    if (editing) update.mutate({ id: editing.id, body });
    else create.mutate(body);
  };
  const saving = create.isPending || update.isPending;

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Plus size={14} />}
            onClick={() => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); }}
          >
            {addLabel}
          </Button>
        }
      >
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody className={styles.bodyGrid}>
        {rules.length === 0 ? (
          <EmptyState icon={<ShieldX size={20} />} title="No access control rules yet" description="Add a rule to allow, deny, or apply token access by URL path and IP." />
        ) : (
          <div className={styles.rulesScroll}><div className={styles.rules}>
            {rules.map((r) => (
              <div key={r.id} className={styles.ruleRow}>
                <Badge tone="neutral" appearance="subtle" size="sm">{r.priority}</Badge>
                <span className={styles.name}>{r.name}</span>
                <span className={styles.muted} title={matchSummary(r)}>{matchSummary(r)}</span>
                <ValueListCell value={r.ip_restriction} />
                <Badge tone={r.enabled ? 'success' : 'neutral'} appearance="subtle" size="sm">
                  {r.enabled ? 'Normal' : 'Disabled'}
                </Badge>
                <div className={styles.actions}>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(r)}>
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate({ rule: r, enabled: !r.enabled })}
                  >
                    {r.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setPendingDelete(r)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div></div>
        )}

      </CardBody>

      <Dialog open={open} onOpenChange={(o) => { if (!o) closeForm(); }}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${title} policy` : `Add ${title} policy`}</DialogTitle>
            <DialogDescription>
              Configure match conditions, restrictions, and priority for this access control policy.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className={styles.dialogForm}>
              <Field label="Policy name" required>
                {(p) => <Input {...p} value={form.name} placeholder="my-policy" onChange={(e) => set('name', e.target.value)} />}
              </Field>

              <Field label="When incoming requests match" description="Field · operator · values (comma-separated).">
                {() => (
                  <div className={styles.matchRow}>
                    <Select value={form.field} onValueChange={(v) => set('field', v)}>
                      <SelectTrigger className={styles.fieldSelect}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={form.operator} onValueChange={(v) => set('operator', v)}>
                      <SelectTrigger className={styles.opSelect}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className={styles.grow} placeholder="/content, /api" value={form.values} onChange={(e) => set('values', e.target.value)} />
                  </div>
                )}
              </Field>

              <div className={styles.grid3}>
                <Field label="GEO restriction" description="2-letter ISO codes. Enter to add; × to remove.">
                  {() => (
                    <ChipListInput
                      ariaLabel="GEO restriction"
                      placeholder="SG, GB"
                      value={splitValues(form.location)}
                      onChange={(arr) => set('location', arr.join(', '))}
                    />
                  )}
                </Field>
                <Field label="IP / Network restriction" description="CIDR list. Enter to add; × to remove.">
                  {() => (
                    <ChipListInput
                      ariaLabel="IP / Network restriction"
                      placeholder="1.1.1.1, 10.0.0.0/24"
                      value={splitValues(form.subnet)}
                      onChange={(arr) => set('subnet', arr.join(', '))}
                    />
                  )}
                </Field>
                <Field label="Priority" description="Higher number = higher priority.">
                  {(p) => <Input {...p} type="number" value={form.priority} onChange={(e) => set('priority', e.target.value)} />}
                </Field>
              </div>

              {type === 'deny' && (
                <label className={styles.checkLabel}>
                  <Checkbox checked={form.anonymousIp} onCheckedChange={(v) => set('anonymousIp', Boolean(v))} />
                  Apply to anonymous IPs
                </label>
              )}

              {type === 'token' && (
                <Field label="Token secret" required description="HMAC secret for signed access.">
                  {() => (
                    <div className={styles.matchRow}>
                      <Input className={styles.grow} value={form.tokenSecret} onChange={(e) => set('tokenSecret', e.target.value)} placeholder="Token secret…" />
                      <IconButton variant="ghost" size="sm" aria-label="Generate token" title="Generate random secret" onClick={() => set('tokenSecret', generateToken())}>
                        <RefreshCw size={15} />
                      </IconButton>
                    </div>
                  )}
                </Field>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button disabled={!valid || saving} onClick={submit}>
              {saving ? 'Saving…' : editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete policy?"
        description={`Remove "${pendingDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Card>
  );
}

const RL_DEFAULT: WaapRateLimit = { time_frame: 60, req_limit: 1, ignore_path: false, ignore_query: false };

function BoolSelect({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <Select value={value ? 'true' : 'false'} onValueChange={(v) => onChange(v === 'true')} disabled={disabled}>
      <SelectTrigger className={styles.boolSelect}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="false">False</SelectItem>
        <SelectItem value="true">True</SelectItem>
      </SelectContent>
    </Select>
  );
}

/** Rate Limit — a table section on the Access Control tab (Request / Time Window
 *  / Ignore URL Path / Ignore Query String / Actions). The "Info" pop-over
 *  explains the ignore flags. Max requests per user IP in a time window. */
function RateLimitSection({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data, isLoading } = useWaapRateLimit(domainId);
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [form, setForm] = useState<WaapRateLimit>(RL_DEFAULT);
  const set = <K extends keyof WaapRateLimit>(k: K, v: WaapRateLimit[K]) => setForm((f) => ({ ...f, [k]: v }));
  const save = useSaveWaapRateLimit(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rate limit saved' }); setEditing(false); },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const del = useDeleteWaapRateLimit(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rate limit deleted' }); setConfirmDel(false); },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });
  const startEdit = (base: WaapRateLimit) => { setForm(base); setEditing(true); };

  // Effective field values: the form while editing, the saved rule otherwise.
  const v = editing ? form : (data ?? RL_DEFAULT);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className={styles.titleRow}>
            Rate Limit
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className={styles.infoBtn}>
                  <Info size={14} /> Info
                </button>
              </PopoverTrigger>
              <PopoverContent className={styles.infoContent}>
                <div className={styles.infoPop}>
                  <p className={styles.infoLead}>
                    Configure the rate limit for the number of requests under the same user IP to be passed in a particular time window.
                  </p>
                  <ul className={styles.infoList}>
                    <li>
                      <b>Ignore URL Path</b> — When the ignore URL path is configured as true, all requests from all user IPs will be counted. When the ignore URL path is configured as false, different URL requests from the same user IP will be counted separately.
                    </li>
                    <li>
                      <b>Ignore Query String</b> — When the ignore query string is configured as true, all requests from any user IP will be counted. When the ignore query string is configured as false, different URL requests from the same user IP will be counted separately.
                    </li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </span>
        </CardTitle>
      </CardHeader>
      <CardBody className={styles.rlScroll}>
        {isLoading ? (
          <Skeleton height={48} />
        ) : (
          <div className={styles.rlGrid}>
            <div className={styles.rlHead}>
              <span>Request</span>
              <span>Time Window</span>
              <span>Ignore URL Path</span>
              <span>Ignore Query String</span>
              <span>Actions</span>
            </div>
            {!data && !editing ? (
              <div className={styles.rlCreate}>
                <Button variant="ghost" size="sm" startIcon={<Plus size={14} />} onClick={() => startEdit(RL_DEFAULT)}>
                  Create RateLimit Rule
                </Button>
              </div>
            ) : (
              <div className={styles.rlRow}>
                <div className={styles.rlCell}>
                  <Input type="number" min={1} className={styles.rlNum} disabled={!editing} value={v.req_limit} onChange={(e) => set('req_limit', Number(e.target.value))} />
                  <span className={styles.nowrap}>requests /</span>
                </div>
                <div className={styles.rlCell}>
                  <Input type="number" min={1} className={styles.rlNum} disabled={!editing} value={v.time_frame} onChange={(e) => set('time_frame', Number(e.target.value))} />
                  <span className={styles.nowrap}>seconds per user IP</span>
                </div>
                <BoolSelect value={v.ignore_path} disabled={!editing} onChange={(val) => set('ignore_path', val)} />
                <BoolSelect value={v.ignore_query} disabled={!editing} onChange={(val) => set('ignore_query', val)} />
                <div className={styles.rlActions}>
                  {editing ? (
                    <>
                      <Button variant="ghost" size="sm" disabled={save.isPending} onClick={() => save.mutate(form)}>Save</Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => startEdit(data!)}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(true)}>Delete</Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardBody>

      <ConfirmDialog
        open={confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(false)}
        title="Delete rate limit?"
        description="Remove the rate-limit rule for this website?"
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => del.mutate()}
      />
    </Card>
  );
}

/**
 * Access Control — website-detail tab. WhiteList (allow) / Blacklist (deny) /
 * Token Secret Access (token), backed by the private CDN portal
 * policy_access_controls. List + Create + Enable/Disable + Delete are wired.
 * Website fixed by the detail route (`:domainId`).
 */
export function AccessControlTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const { data, isLoading } = useWaapAccessControls(domainId);

  const byType = useMemo(() => {
    const m: Record<string, WaapAccessControlRule[]> = { allow: [], deny: [], token: [] };
    for (const r of data ?? []) (m[r.type] ??= []).push(r);
    return m;
  }, [data]);

  if (isLoading) return <Skeleton height={240} />;

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Access Control</CardTitle>
        <CardDescription>Allow / deny / token-secured access by URL path, IP and geo.</CardDescription>
      </CardHeader>
      <CardBody>
      <div className={styles.stack}>
        {SECTIONS.map((s) => (
          <PolicySection
            key={s.type}
            domainId={domainId ?? ''}
            type={s.type}
            title={s.title}
            addLabel={s.add}
            rules={byType[s.type] ?? []}
          />
        ))}
        <RateLimitSection domainId={domainId ?? ''} />
      </div>
      </CardBody>
    </Card>
  );
}
